import { Drawer } from "theme";
import { animateSequence } from "vendor";

const MOBILE_MEDIA = "(max-width: 699px)";

// Contract between a card and the "Style with" block that wraps it: the block mirrors
// the open card's title and price into its shared label row.
const CARD_TOGGLE_EVENT = "shop-the-look-card:toggle";

// Fired the instant a size is chosen, carrying what the card already shows on screen. The
// drawer paints its confirmation panel from this straight away instead of waiting for the add
// request to answer with a fresh image URL, which is what made the picture lag behind the tap.
const CARD_ADDING_EVENT = "shop-the-look-card:adding";

if (!customElements.get("shop-the-look-card")) {
    class ShopTheLookCard extends HTMLElement {
        static SELECTORS = {
            toggle: ".stl-card__toggle",
            panel: ".stl-card__sizes",
            sizeButtons: ".stl-card__size[data-variant-id]",
            sizeGuide: "[data-stl-size-guide]",
            form: 'form[action*="/cart/add"]',
            idInput: 'input[name="id"]',
            guideDrawer: "x-drawer[data-size-guide]",
            guidePopover: "x-popover[data-size-guide]",
            image: ".stl-card__image",
            title: ".stl-card__title",
            price: ".stl-card__price"
        };

        static CLASSES = { open: "stl-card--open", anyPanelOpen: "stl-panel-open" };

        static ATTRIBUTES = { uniqueSize: "data-unique-size", expanded: "aria-expanded", handle: "data-handle" };

        // Alternate product template returning just that product's size guide
        static GUIDE_VIEW = "size-guide";

        // The theme shows the guide as a popover below this width and as a drawer above it,
        // matching its own product-info tabs.
        static GUIDE_POPOVER_MEDIA = "(max-width: 999px)";

        // Mirrors "some size selector is open" onto <html>. On mobile the selector is a fixed
        // sheet, but it sits in the same stacking context as the sticky buy bar, so the two
        // can only be ordered by z-index number. This flag lets CSS take the bar out of that
        // contest instead, which survives the bar's z-index being raised.
        static syncPanelOpenFlag() {
            document.documentElement.classList.toggle(
                ShopTheLookCard.CLASSES.anyPanelOpen,
                document.querySelector(`.${ShopTheLookCard.CLASSES.open}`) !== null
            );
        }

        connectedCallback() {
            this._abort = new AbortController();
            this._initElements();
            this._attachListeners();
        }

        disconnectedCallback() {
            this._abort?.abort();
            // The drawer replaces its whole grid on every add: a card torn out while its
            // panel was open must not leave the flag stuck on.
            ShopTheLookCard.syncPanelOpenFlag();
            // The fetched guide lives on the body, so it outlives the card unless removed here
            this._sizeGuideParts?.forEach((part) => part.remove());
            this._sizeGuideParts = null;
            this._sizeGuideRequest = null;
        }

        get isOpen() {
            return this.classList.contains(ShopTheLookCard.CLASSES.open);
        }

        togglePanel() {
            if (this.hasAttribute(ShopTheLookCard.ATTRIBUTES.uniqueSize)) {
                this._addVariant(this._idInput?.value);
                return;
            }

            this.isOpen ? this.closePanel() : this.openPanel();
        }

        openPanel() {
            document.querySelectorAll(`.${ShopTheLookCard.CLASSES.open}`).forEach((card) => {
                if (card !== this && card instanceof ShopTheLookCard) {
                    card.closePanel();
                }
            });

            this.classList.add(ShopTheLookCard.CLASSES.open);
            this._toggle?.setAttribute(ShopTheLookCard.ATTRIBUTES.expanded, "true");
            this._publishState();
        }

        closePanel() {
            this.classList.remove(ShopTheLookCard.CLASSES.open);
            this._toggle?.setAttribute(ShopTheLookCard.ATTRIBUTES.expanded, "false");
            this._publishState();
        }

        // Both readers of "a panel opened or closed" are updated from one place: the document
        // flag that CSS keys off, and the event the surrounding blocks listen to.
        _publishState() {
            ShopTheLookCard.syncPanelOpenFlag();
            this.dispatchEvent(new CustomEvent(CARD_TOGGLE_EVENT, { bubbles: true, detail: { open: this.isOpen } }));
        }

        _initElements() {
            this._toggle = this.querySelector(ShopTheLookCard.SELECTORS.toggle);
            this._sizeGuideButton = this.querySelector(ShopTheLookCard.SELECTORS.sizeGuide);
            this._form = this.querySelector(ShopTheLookCard.SELECTORS.form);
            this._idInput = this._form?.querySelector(ShopTheLookCard.SELECTORS.idInput);
        }

        _attachListeners() {
            const { signal } = this._abort;

            this._toggle?.addEventListener("click", () => this.togglePanel(), { signal });
            this._sizeGuideButton?.addEventListener("click", () => this._openSizeGuide(), { signal });

            this.querySelectorAll(ShopTheLookCard.SELECTORS.sizeButtons).forEach((button) => {
                button.addEventListener("click", () => this._addVariant(button.dataset.variantId), { signal });
            });

            document.addEventListener("click", (event) => this._onOutsideClick(event), { signal });
            document.addEventListener("keydown", (event) => this._onKeydown(event), { capture: true, signal });
        }

        // The page only carries the main product's size guide, so this product's own guide is
        // fetched on first use and parked on the body for reuse.
        async _openSizeGuide() {
            const handle = this.getAttribute(ShopTheLookCard.ATTRIBUTES.handle);

            if (!handle) {
                return;
            }

            // Cache the promise, not its result: repeated clicks while the first request is in
            // flight must not each append their own copy of the guide.
            this._sizeGuideRequest ??= ShopTheLookCard.loadSizeGuide(handle);
            this._sizeGuideParts = await this._sizeGuideRequest;

            // No guide exists for this product's type: drop the control rather than leave a
            // button that does nothing.
            if (!this._sizeGuideParts) {
                this._sizeGuideButton?.remove();
                this._sizeGuideButton = null;
                return;
            }

            const wantsPopover = window.matchMedia(ShopTheLookCard.GUIDE_POPOVER_MEDIA).matches;
            const selector = wantsPopover ? ShopTheLookCard.SELECTORS.guidePopover : ShopTheLookCard.SELECTORS.guideDrawer;
            const guide = this._sizeGuideParts.find((part) => part.matches(selector));

            // `open` is read-only on the theme's dialog element; show() is the way in, and the
            // activator is what it returns focus to on close.
            guide?.show({ activator: this._sizeGuideButton });
        }

        // Resolves to this product's [drawer, popover], already mounted, or null when the
        // response carries no guide — products whose type has no size table.
        static async loadSizeGuide(handle) {
            let markup;

            try {
                const response = await fetch(`${Shopify.routes.root}products/${handle}?view=${ShopTheLookCard.GUIDE_VIEW}`);

                if (!response.ok) {
                    return null;
                }

                markup = await response.text();
            } catch {
                return null;
            }

            const parsed = new DOMParser().parseFromString(markup, "text/html");
            const parts = Array.from(parsed.querySelectorAll(
                `${ShopTheLookCard.SELECTORS.guideDrawer}, ${ShopTheLookCard.SELECTORS.guidePopover}`
            ));

            if (!parts.length) {
                return null;
            }

            // Mounted on the body rather than inside the card: the guide has to escape the
            // product column's stacking context, and the theme's dialogs move themselves to the
            // body on first show anyway — which is why the elements are tracked individually
            // instead of through a wrapper that they would leave behind.
            document.body.append(...parts);
            window.initSizeGuides?.(document.body);

            return parts;
        }

        _onOutsideClick(event) {
            if (this.isOpen && !this.contains(event.target)) {
                this.closePanel();
            }
        }

        _onKeydown(event) {
            if (event.key === "Escape" && this.isOpen) {
                // Capture-phase stop so an open size panel closes without also closing
                // the surrounding drawer.
                event.stopPropagation();
                this.closePanel();
            }
        }

        _addVariant(variantId) {
            if (!variantId || !this._form || !this._idInput) {
                return;
            }

            this._idInput.value = variantId;
            this._announceAdding(variantId);
            this.closePanel();
            this._form.requestSubmit();
        }

        _announceAdding(variantId) {
            this.dispatchEvent(new CustomEvent(CARD_ADDING_EVENT, {
                bubbles: true,
                detail: this._buildPreview(variantId)
            }));
        }

        // Everything the confirmation panel shows is already rendered in this card, so it can be
        // handed over as-is — no request, and the image is one the browser has already decoded.
        _buildPreview(variantId) {
            const image = this.querySelector(ShopTheLookCard.SELECTORS.image);
            const sizeButton = this.querySelector(`[data-variant-id="${variantId}"]`);

            return {
                // currentSrc, not src: the resolution the browser actually picked from srcset,
                // which is the one guaranteed to be in cache.
                imageUrl: image?.currentSrc || image?.src || "",
                title: this.querySelector(ShopTheLookCard.SELECTORS.title)?.textContent.trim() ?? "",
                priceHtml: this.querySelector(ShopTheLookCard.SELECTORS.price)?.innerHTML ?? "",
                // Empty for unique-size products, which have no size row to read
                size: sizeButton?.textContent.trim() ?? ""
            };
        }
    }

    customElements.define("shop-the-look-card", ShopTheLookCard);
}

if (!customElements.get("style-with-block")) {
    // Product-page look strip. On desktop the cards are bare and this block owns a single
    // label row showing the title and price of whichever card the user is pointing at, or
    // of the card whose size panel is open. On mobile the row is hidden and each card
    // renders its own labels, so nothing here has a visible effect.
    class StyleWithBlock extends HTMLElement {
        static SELECTORS = {
            cards: "shop-the-look-card",
            label: "[data-style-with-label]",
            labelTitle: "[data-style-with-label-title]",
            labelPrice: "[data-style-with-label-price]",
            cardTitle: ".stl-card__title",
            cardPrice: ".stl-card__price"
        };

        static CLASSES = { active: "style-with__label--active" };

        connectedCallback() {
            this._abort = new AbortController();
            // An open size panel pins the row; pointing at a card only previews it.
            this._pinnedCard = null;
            this._pointedCard = null;
            this._initElements();
            this._attachListeners();
        }

        disconnectedCallback() {
            this._abort?.abort();
        }

        _initElements() {
            this._label = this.querySelector(StyleWithBlock.SELECTORS.label);
            this._labelTitle = this.querySelector(StyleWithBlock.SELECTORS.labelTitle);
            this._labelPrice = this.querySelector(StyleWithBlock.SELECTORS.labelPrice);
        }

        _attachListeners() {
            const { signal } = this._abort;

            this.addEventListener(CARD_TOGGLE_EVENT, (event) => this._onCardToggle(event), { signal });

            this.querySelectorAll(StyleWithBlock.SELECTORS.cards).forEach((card) => {
                // Mouse only: a tap would leave the row stuck on the last card touched.
                card.addEventListener("pointerenter", (event) => {
                    if (event.pointerType === "mouse") this._point(card);
                }, { signal });
                card.addEventListener("pointerleave", (event) => {
                    if (event.pointerType === "mouse") this._point(null);
                }, { signal });

                // Keyboard equivalent. Focus moving within the card (toggle -> size button)
                // must not blank the row, hence the relatedTarget check.
                card.addEventListener("focusin", () => this._point(card), { signal });
                card.addEventListener("focusout", (event) => {
                    if (!card.contains(event.relatedTarget)) this._point(null);
                }, { signal });
            });
        }

        _onCardToggle(event) {
            // Opening a card closes the previous one, so a close event can arrive from a
            // card that is no longer the pinned one — only the pinned card may unpin.
            if (event.detail.open) {
                this._pinnedCard = event.target;
            } else if (this._pinnedCard === event.target) {
                this._pinnedCard = null;
            }

            this._render();
        }

        _point(card) {
            this._pointedCard = card;
            this._render();
        }

        _render() {
            const card = this._pinnedCard || this._pointedCard;

            if (!this._label || !card) {
                this._label?.classList.remove(StyleWithBlock.CLASSES.active);
                return;
            }

            this._labelTitle.textContent = card.querySelector(StyleWithBlock.SELECTORS.cardTitle)?.textContent.trim() ?? "";
            // innerHTML: the price can carry a struck-through compare-at span
            this._labelPrice.innerHTML = card.querySelector(StyleWithBlock.SELECTORS.cardPrice)?.innerHTML ?? "";
            this._label.classList.add(StyleWithBlock.CLASSES.active);
        }
    }

    customElements.define("style-with-block", StyleWithBlock);
}

if (!customElements.get("shop-the-look-drawer")) {
    class ShopTheLookDrawer extends Drawer {
        static SELECTORS = {
            addedImage: "[data-stl-added-image]",
            addedTitle: "[data-stl-added-title]",
            addedPrice: "[data-stl-added-price]",
            addedCompare: "[data-stl-added-compare]",
            addedSize: "[data-stl-added-size]",
            look: "[data-stl-look]",
            lookGrid: "[data-stl-look-grid]",
            grid: ".stl-drawer__grid",
            openCard: ".stl-card--open"
        };

        static ATTRIBUTES = { lookHandle: "data-look-handle" };

        static EVENTS = {
            variantAdd: "variant:add",
            cartChange: "cart:change",
            cartError: "cart:error",
            toastShow: "toast:show"
        };

        // Alternate product template returning just the look grid (templates/product.stl-look.liquid)
        static LOOK_VIEW = "stl-look";

        // Close the drawer when the user has not interacted with it for this long. 
        static AUTO_CLOSE_DELAY = 5000;

        // A toast fired within this window after an add is the theme's own
        // "added to cart" notification, redundant with this drawer.
        static TOAST_SUPPRESS_WINDOW = 1000;

        // baseEvent used when re-dispatching cart:change so the theme's cart
        // drawer re-renders its content without auto-opening (it only opens on
        // baseEvent === "variant:add").
        static SUPPRESSED_BASE_EVENT = "shop-the-look:add";

        // Variant titles that must not be shown as a size line.
        static HIDDEN_SIZE_TITLES = ["Default Title", "UNIQUE SIZE"];

        connectedCallback() {
            super.connectedCallback();

            this._look = this.querySelector(ShopTheLookDrawer.SELECTORS.look);
            this._lookGrid = this.querySelector(ShopTheLookDrawer.SELECTORS.lookGrid);
            // Seeded only on the first connect: the grid is server-rendered for the page's
            // main product, so adding that product needs no fetch. The theme re-slots the
            // drawer when it opens, which re-runs this — re-seeding there would forget an
            // already-swapped look and cancel a request in flight.
            this._lookHandle ??= this.getAttribute(ShopTheLookDrawer.ATTRIBUTES.lookHandle);

            const { signal } = this.abortController;
            document.addEventListener(ShopTheLookDrawer.EVENTS.variantAdd, this._onVariantAdd.bind(this), { capture: true, signal });
            document.addEventListener(ShopTheLookDrawer.EVENTS.cartChange, this._onCartChange.bind(this), { capture: true, signal });
            document.addEventListener(ShopTheLookDrawer.EVENTS.cartError, this._onCartError.bind(this), { capture: true, signal });
            document.addEventListener(ShopTheLookDrawer.EVENTS.toastShow, this._onToastShow.bind(this), { capture: true, signal });

            // Interaction listeners go on the content part only — the overlay part
            // covers the whole viewport, so listening on the host would treat any
            // mouse move on the page as an interaction and the drawer would never
            // auto-close. Events from slotted children bubble through the content part.
            const content = this.getShadowPartByName("content");
            ["pointerdown", "touchstart", "keydown", "wheel"].forEach((eventName) => {
                content.addEventListener(eventName, () => this._resetAutoClose(), { passive: true, signal });
            });
            // Mouse only: hovering the panel pauses the countdown, leaving restarts it.
            // Touch pointers skip this — pointerleave is unreliable there and would
            // leave the drawer open forever after a tap.
            content.addEventListener("pointerenter", (event) => {
                if (event.pointerType === "mouse") {
                    this._hovered = true;
                    this._clearAutoClose();
                }
            }, { signal });
            content.addEventListener("pointerleave", (event) => {
                if (event.pointerType === "mouse") {
                    this._hovered = false;
                    this._startAutoClose();
                }
            }, { signal });

            // Opening or closing a size selector changes whether the countdown may run at
            // all; _startAutoClose decides, so both directions go through the same call.
            this.addEventListener(CARD_TOGGLE_EVENT, () => this._startAutoClose(), { signal });

            // On document, not on this element: the Style with strip lives outside the drawer, so
            // listening here too means the drawer is already populated when it opens rather than
            // filling in afterwards.
            document.addEventListener(CARD_ADDING_EVENT, (event) => this._renderPreview(event.detail), { capture: true, signal });
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._clearAutoClose();
        }

        get initialFocus() {
            return false;
        }

        get _isMobile() {
            return window.matchMedia(MOBILE_MEDIA).matches;
        }

        createEnterAnimationControls() {
            if (!this._isMobile) {
                return super.createEnterAnimationControls();
            }

            this.getShadowPartByName("content").style.marginInlineStart = "0";
            return animateSequence([
                [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.25, ease: "easeInOut" }],
                [
                    this.getShadowPartByName("content"),
                    { opacity: [0, 1], transform: ["translateY(100%)", "translateY(0)"] },
                    { duration: 0.45, at: "-0.15", ease: [0.86, 0, 0.07, 1] }
                ]
            ]);
        }

        createLeaveAnimationControls() {
            if (!this._isMobile) {
                return super.createLeaveAnimationControls();
            }

            return animateSequence([
                [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.45, ease: "easeInOut" }],
                [
                    this.getShadowPartByName("content"),
                    { opacity: [1, 0], transform: ["translateY(0)", "translateY(100%)"] },
                    { duration: 0.45, at: "<", ease: [0.86, 0, 0.07, 1] }
                ]
            ]);
        }

        _onVariantAdd(event) {
            const item = event.detail?.items?.[0];

            if (item) {
                this._suppressToastUntil = performance.now() + ShopTheLookDrawer.TOAST_SUPPRESS_WINDOW;
                this._renderAddedItem(item);
                this._loadLook(item.handle);
            }
        }

        // Swaps the grid to the look of the product just added, so adding from the drawer
        // walks the customer onwards instead of showing the same suggestions again.
        async _loadLook(handle) {
            if (!handle || handle === this._lookHandle) {
                return;
            }

            // Claimed synchronously, before any await: _lookHandle doubles as the guard
            // against a slower earlier request overwriting a faster later one.
            const previous = this._lookHandle;
            this._lookHandle = handle;

            const markup = await this._fetchLook(handle);

            // A later add claimed the grid while this request was in flight
            if (this._lookHandle !== handle) {
                return;
            }

            if (markup === null) {
                // Fetch failed: keep the current grid, and release the claim so that adding
                // the same product again retries instead of being skipped as a no-op.
                this._lookHandle = previous;
                return;
            }

            // Re-read: the theme re-slots the drawer when it opens, which can swap these
            // nodes out from under a request that started before the drawer was shown.
            this._look = this.querySelector(ShopTheLookDrawer.SELECTORS.look);
            this._lookGrid = this.querySelector(ShopTheLookDrawer.SELECTORS.lookGrid);

            if (!this._look || !this._lookGrid) {
                return;
            }

            // Pick the grid out of the response rather than trusting it wholesale: a product
            // whose template ignores ?view= (gift cards, for one) answers with a whole page.
            const grid = new DOMParser().parseFromString(markup, "text/html").querySelector(ShopTheLookDrawer.SELECTORS.grid);

            this._lookGrid.replaceChildren(...(grid ? [grid] : []));
            // No look products for this one: hide the heading too
            this._look.hidden = !grid;
        }

        // The look grid markup for a product, or null when it could not be fetched
        async _fetchLook(handle) {
            try {
                const response = await fetch(`${Shopify.routes.root}products/${handle}?view=${ShopTheLookDrawer.LOOK_VIEW}`);

                return response.ok ? await response.text() : null;
            } catch {
                return null;
            }
        }

        // A failed add must not leave this panel up: it says "added to your cart" about whatever
        // was added last, so after a rejected add it asserts the wrong product — the customer
        // reads the error toast over a picture of something else.
        //
        // Shopify answers a partial add with 422 ("Only N items were added…") even though it did
        // add N, so the theme takes its error branch and never fires variant:add or cart:change.
        // Nothing here can refresh the panel from that response; closing is the honest outcome,
        // and the toast carries the message.
        _onCartError() {
            this._clearAutoClose();

            if (this.open) {
                this.hide();
            }
        }

        // The theme fires its own "added to cart" toast right after variant:add;
        // this drawer replaces it, so swallow it. Error toasts stay untouched.
        _onToastShow(event) {
            const isAddToCartToast = this._suppressToastUntil && performance.now() < this._suppressToastUntil;

            if (isAddToCartToast && event.detail?.tone !== "error") {
                event.stopImmediatePropagation();
            }
        }

        _onCartChange(event) {
            if (event.detail?.baseEvent !== "variant:add") {
                return;
            }

            // Take over the theme's default behaviour: stop the original event so the
            // cart drawer does not open, then re-dispatch it with a different baseEvent
            // so every cart-bound section still refreshes its content.
            event.stopImmediatePropagation();
            document.documentElement.dispatchEvent(
                new CustomEvent(ShopTheLookDrawer.EVENTS.cartChange, {
                    bubbles: true,
                    detail: { ...event.detail, baseEvent: ShopTheLookDrawer.SUPPRESSED_BASE_EVENT }
                })
            );

            if (!this.open) {
                this.show();
            }

            this._startAutoClose();
        }

        _startAutoClose() {
            this._clearAutoClose();

            // Never count down while the customer is engaged with the drawer: pointer over
            // it, or a size selector open inside it. Picking a size is slow and generates no
            // pointer movement to reset the timer, and on touch there is no hover to pause it.
            if (this._hovered || this.querySelector(ShopTheLookDrawer.SELECTORS.openCard)) {
                return;
            }

            this._autoCloseTimer = setTimeout(() => {
                this._autoCloseTimer = null;
                if (this.open) {
                    this.hide();
                }
            }, ShopTheLookDrawer.AUTO_CLOSE_DELAY);
        }

        _resetAutoClose() {
            if (this._autoCloseTimer) {
                this._startAutoClose();
            }
        }

        _clearAutoClose() {
            if (this._autoCloseTimer) {
                clearTimeout(this._autoCloseTimer);
                this._autoCloseTimer = null;
            }
        }

        _renderAddedItem(item) {
            const image = this.querySelector(ShopTheLookDrawer.SELECTORS.addedImage);
            const title = this.querySelector(ShopTheLookDrawer.SELECTORS.addedTitle);
            const price = this.querySelector(ShopTheLookDrawer.SELECTORS.addedPrice);
            const size = this.querySelector(ShopTheLookDrawer.SELECTORS.addedSize);

            const imageUrl = item.featured_image?.url || item.image;
            if (image) {
                image.hidden = !imageUrl;
                if (imageUrl) {
                    // Keep whatever the preview already put there when it is the same picture:
                    // the cart payload gives an absolute CDN url while the card renders a
                    // resized one through the storefront, so the strings differ but the file is
                    // identical. Reassigning src would re-download it and flicker — exactly what
                    // the preview exists to avoid.
                    if (ShopTheLookDrawer._imageFile(image.src) !== ShopTheLookDrawer._imageFile(imageUrl)) {
                        image.src = imageUrl;
                    }

                    image.alt = item.product_title || "";
                }
            }

            if (title) {
                title.textContent = item.product_title || "";
            }

            if (price) {
                price.textContent = this._formatMoney(item.final_price ?? item.price);
            }

            const compare = this.querySelector(ShopTheLookDrawer.SELECTORS.addedCompare);
            if (compare) {
                const isOnSale = typeof item.original_price === "number" && item.original_price > (item.final_price ?? item.price);
                compare.hidden = !isOnSale;
                compare.textContent = isOnSale ? this._formatMoney(item.original_price) : "";
            }

            this._renderSize(item.variant_title);
        }

        // Painted from the card the customer just tapped, before the add request is even sent:
        // same image URL, already decoded, so the panel changes with the tap instead of after
        // the round trip. _renderAddedItem overwrites all of it with the authoritative values
        // as soon as variant:add lands.
        _renderPreview(preview) {
            const image = this.querySelector(ShopTheLookDrawer.SELECTORS.addedImage);
            const title = this.querySelector(ShopTheLookDrawer.SELECTORS.addedTitle);
            const price = this.querySelector(ShopTheLookDrawer.SELECTORS.addedPrice);
            const compare = this.querySelector(ShopTheLookDrawer.SELECTORS.addedCompare);

            if (image) {
                image.hidden = !preview.imageUrl;
                if (preview.imageUrl) {
                    image.src = preview.imageUrl;
                    image.alt = preview.title;
                }
            }

            if (title) {
                title.textContent = preview.title;
            }

            if (price) {
                // The card's own markup already carries any struck-through compare-at price, so
                // the drawer's separate compare slot stays out of the way until the real render.
                price.innerHTML = preview.priceHtml;
            }

            if (compare) {
                compare.hidden = true;
            }

            this._renderSize(preview.size);
        }

        // Shopify's hashed filename is the stable identity of an image across hosts and
        // resize parameters, so it is what tells two urls apart.
        static _imageFile(url) {
            if (!url) {
                return "";
            }

            try {
                return new URL(url, location.href).pathname.split("/").pop();
            } catch {
                return "";
            }
        }

        _renderSize(variantTitle) {
            const size = this.querySelector(ShopTheLookDrawer.SELECTORS.addedSize);

            if (!size) {
                return;
            }

            const showSize = variantTitle && !ShopTheLookDrawer.HIDDEN_SIZE_TITLES.includes(variantTitle);
            size.hidden = !showSize;

            if (showSize) {
                size.textContent = size.dataset.template
                    ? size.dataset.template.replace("[size]", variantTitle)
                    : variantTitle;
            }
        }

        _formatMoney(cents) {
            if (typeof cents !== "number") {
                return "";
            }

            const format = window.themeVariables?.settings?.moneyFormat || "{{ amount }}";
            const amount = cents / 100;
            const placeholder = format.match(/{{\s*(\w+)\s*}}/)?.[1] || "amount";

            let value;
            switch (placeholder) {
                case "amount_no_decimals":
                case "amount_no_decimals_with_comma_separator":
                    value = Math.round(amount).toString();
                    break;
                case "amount_with_comma_separator":
                    value = amount.toFixed(2).replace(".", ",");
                    break;
                default:
                    value = amount.toFixed(2);
            }

            // Match the theme's money_without_trailing_zeros rendering.
            value = value.replace(/[.,]00$/, "");

            return format.replace(/{{\s*\w+\s*}}/, value).trim();
        }
    }

    customElements.define("shop-the-look-drawer", ShopTheLookDrawer);
}
